import { escapeHtml } from '../utils/dom-utils';

export function showFieldSelector(allKeys: string[]): Promise<string[]> {
  return new Promise((resolve, reject) => {
    // Build overlay
    const overlay = document.createElement('div');
    overlay.className = 'field-selector-overlay';

    const modal = document.createElement('div');
    modal.className = 'field-selector-modal';

    // Header
    const header = document.createElement('div');
    header.className = 'field-selector-header';
    header.innerHTML = `
      <h3>Felder zum Import auswählen</h3>
      <button class="field-selector-close">&times;</button>
    `;

    // Controls
    const controls = document.createElement('div');
    controls.className = 'field-selector-controls';
    controls.innerHTML = `
      <button class="field-selector-btn" data-action="select-all">Alle auswählen</button>
      <button class="field-selector-btn" data-action="deselect-all">Alle abwählen</button>
      <input type="text" class="field-selector-search" placeholder="Felder suchen...">
    `;

    // Field list
    const list = document.createElement('div');
    list.className = 'field-selector-list';

    for (const key of allKeys) {
      const item = document.createElement('div');
      item.className = 'field-selector-item';
      item.dataset.key = key;
      item.innerHTML = `
        <label>
          <input type="checkbox" value="${escapeHtml(key)}" checked>
          <span>${escapeHtml(key)}</span>
        </label>
      `;
      list.appendChild(item);
    }

    // Footer
    const footer = document.createElement('div');
    footer.className = 'field-selector-footer';
    footer.innerHTML = `
      <span class="field-selector-count"></span>
      <div class="field-selector-actions">
        <button class="field-selector-btn-cancel">Abbrechen</button>
        <button class="field-selector-btn-import">Importieren</button>
      </div>
    `;

    // Assemble
    modal.appendChild(header);
    modal.appendChild(controls);
    modal.appendChild(list);
    modal.appendChild(footer);
    overlay.appendChild(modal);

    // Helpers
    const getCheckboxes = (): HTMLInputElement[] =>
      Array.from(list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'));

    const getVisibleCheckboxes = (): HTMLInputElement[] =>
      getCheckboxes().filter(cb => {
        const item = cb.closest('.field-selector-item');
        return item && !item.classList.contains('hidden');
      });

    const updateCount = (): void => {
      const total = allKeys.length;
      const selected = getCheckboxes().filter(cb => cb.checked).length;
      const countEl = footer.querySelector('.field-selector-count');
      if (countEl) {
        countEl.textContent = `Ausgewählt: ${selected} von ${total} Feldern`;
      }
      const importBtn = footer.querySelector<HTMLButtonElement>('.field-selector-btn-import');
      if (importBtn) {
        importBtn.disabled = selected === 0;
      }
    };

    const cleanup = (): void => {
      overlay.remove();
    };

    // Event: checkbox changes
    list.addEventListener('change', updateCount);

    // Event: select all / deselect all
    controls.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.dataset.action;
      if (action === 'select-all') {
        getVisibleCheckboxes().forEach(cb => { cb.checked = true; });
        updateCount();
      } else if (action === 'deselect-all') {
        getVisibleCheckboxes().forEach(cb => { cb.checked = false; });
        updateCount();
      }
    });

    // Event: search
    const searchInput = controls.querySelector<HTMLInputElement>('.field-selector-search');
    if (searchInput) {
      searchInput.addEventListener('input', () => {
        const query = searchInput.value.toLowerCase();
        const items = list.querySelectorAll<HTMLElement>('.field-selector-item');
        items.forEach(item => {
          const key = item.dataset.key || '';
          if (key.toLowerCase().includes(query)) {
            item.classList.remove('hidden');
          } else {
            item.classList.add('hidden');
          }
        });
      });
    }

    // Event: close button
    const closeBtn = header.querySelector('.field-selector-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        cleanup();
        reject(new Error('FIELD_SELECTION_CANCELLED'));
      });
    }

    // Event: cancel
    const cancelBtn = footer.querySelector('.field-selector-btn-cancel');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        cleanup();
        reject(new Error('FIELD_SELECTION_CANCELLED'));
      });
    }

    // Event: import
    const importBtn = footer.querySelector('.field-selector-btn-import');
    if (importBtn) {
      importBtn.addEventListener('click', () => {
        const selected = getCheckboxes()
          .filter(cb => cb.checked)
          .map(cb => cb.value);
        cleanup();
        resolve(selected);
      });
    }

    // Initialize count and add to DOM
    updateCount();
    document.body.appendChild(overlay);

    // Focus search for convenience
    searchInput?.focus();
  });
}
